import { FC, Fragment, useMemo } from 'react';
import {
  ContentWrapper,
  MainTitleContainer,
  ConflictNote,
  HeaderWrapper,
} from 'modals/ShiftModal/src/views/AwardTeamMemberView/ShiftConflictView/ShiftConflictCard.styled';
import { BKJIcon, BKJLoadingSpinner } from '@bookjane2/bookjane-design-library';
import { CSVBorderHeaderStyle } from 'components/InviteTeamMembersByCSVModal/views/ImportViaSpreadsheet/TeamMemberCSVView.styled';
import { useAwardTeamMemberContext } from '../AwardTeamMemberView.data.context';
import { Flex } from 'components/Flex';
import { ShiftConflictCard } from './ShiftConflictCard';
import { getShiftBiddingStatus } from 'utils/shiftStatus';
import { BiddableShiftStatus } from 'components/Tags/Tags.types';
import { useURLParameters } from 'hooks';
import { AwardTeamMemberConflictedShift } from 'modals/ShiftModal/src/views/AwardTeamMemberView/AwardTeamMemberView.constants';

export const ShiftConflictView: FC = () => {
  const { data, isLoading } = useAwardTeamMemberContext();
  const order = data?.order;
  const [{ selectedJanes: rawSelectedJanes }] =
    useURLParameters<{
      selectedJanes?: string;
    }>();

  const selectedJanes = rawSelectedJanes?.split(',');

  const conflictedShifts = useMemo(() => {
    if (!order?.bids || !selectedJanes?.length) {
      return [];
    }

    const conflicts: Array<{
      conflictedShift: AwardTeamMemberConflictedShift;
      janeName: string;
      janeAvatarUrl?: string;
      positionName?: string;
      communityName?: string;
      status: AwardTeamMemberConflictedShift['status'];
      biddingStatus?: Nullable<BiddableShiftStatus>;
      isJaneReleaseAllowed: boolean;
      communityId: string;
    }> = [];

    selectedJanes.forEach((janeId) => {
      const bid = order.bids?.find((bidItem) => bidItem?.jane?.id === janeId);
      if (bid?.conflictedShifts && bid.conflictedShifts.length > 0) {
        bid.conflictedShifts.forEach((conflictedShift) => {
          conflicts.push({
            conflictedShift,
            janeName:
              conflictedShift.mainJane?.firstName && conflictedShift.mainJane?.lastName
                ? `${conflictedShift.mainJane.firstName} ${conflictedShift.mainJane.lastName}`
                : bid.jane
                ? `${bid.jane.firstName} ${bid.jane.lastName}`
                : '',
            janeAvatarUrl:
              conflictedShift.mainJane?.avatarUrls?.origin ||
              bid.jane?.avatarUrls?.origin ||
              undefined,
            positionName:
              conflictedShift.position?.name ||
              bid.jane?.primaryPosition?.details?.name ||
              undefined,
            communityName: conflictedShift.community.name || undefined,
            communityId: conflictedShift.community.displayId,
            status: conflictedShift.status,
            isJaneReleaseAllowed: conflictedShift.isJaneReleaseAllowed,
            biddingStatus: getShiftBiddingStatus({
              status: conflictedShift.status.value,
              biddable: conflictedShift.biddable,
              biddingTimeEndsAt: conflictedShift.biddingTimeEndsAt,
              awardingTimeStartsAt: conflictedShift.awardingTimeStartsAt,
              bids: conflictedShift.bids,
            }),
          });
        });
      }
    });

    return conflicts;
  }, [order?.bids, selectedJanes]);

  return (
    <Fragment>
      <HeaderWrapper borderTop={CSVBorderHeaderStyle}>
        <MainTitleContainer justifyContent="space-between" alignItems="baseline">
          {'Shift Conflict'}
        </MainTitleContainer>
        <ContentWrapper>
          <Flex alignItems="start" gap="8px">
            <Flex margin="2px">
              <BKJIcon iconName="Warning" height="20px" width="20px" />
            </Flex>
            <ConflictNote>
              {
                'This team member is already assigned to another shift. Release the conflicting shift below to continue, or select a different team member.'
              }
            </ConflictNote>
          </Flex>
        </ContentWrapper>

        <Flex flexDirection="column" width="100%" padding="32px" justifyContent="center">
          {isLoading ? (
            <BKJLoadingSpinner variant="Default" />
          ) : (
            conflictedShifts.map((conflict, index) => (
              <ShiftConflictCard
                key={`${conflict.conflictedShift.displayId}-${index}`}
                conflictedShift={conflict.conflictedShift}
                janeName={conflict.janeName}
                janeAvatarUrl={conflict.janeAvatarUrl}
                positionName={conflict.positionName}
                communityName={conflict.communityName}
                status={conflict.status}
                biddingStatus={conflict.biddingStatus}
                isJaneReleaseAllowed={conflict.isJaneReleaseAllowed}
                communityId={conflict.communityId}
              />
            ))
          )}
        </Flex>
      </HeaderWrapper>
    </Fragment>
  );
};
